// Control Systems — System Modeling
// Matches style of ControlSystems.jsx and Magnetism reference page

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1: Mechanical Translational System (mass-spring-damper)
// ─────────────────────────────────────────────────────────────────
function DiagramMechanicalTranslational() {
  const W = 900, H = 340;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mechanical Translational System — Mass-Spring-Damper Model</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <marker id="mt-arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#c0392b"/>
          </marker>
          <marker id="mt-blk" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#333"/>
          </marker>
        </defs>

        {/* ── LEFT PANEL: Physical diagram ── */}
        <rect x={10} y={10} width={430} height={320} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={225} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Physical System</text>

        {/* Wall (hatch) */}
        {[0,1,2,3,4,5,6,7].map(i => (
          <line key={i} x1={30} y1={50+i*18} x2={50} y2={68+i*18} stroke="#888" strokeWidth="1.2"/>
        ))}
        <line x1={30} y1={50} x2={30} y2={200} stroke="#333" strokeWidth="2.5"/>

        {/* Spring (zigzag) between wall and mass */}
        {(() => {
          const pts = [[30,100]];
          const segs = 8, x0=30, x1=140, y=100;
          const dx = (x1-x0)/(segs);
          for(let i=0;i<segs;i++){
            pts.push([x0+dx*(i+0.5), i%2===0? y-16 : y+16]);
            pts.push([x0+dx*(i+1), y]);
          }
          const d = pts.map((p,i)=> `${i===0?'M':'L'}${p[0]},${p[1]}`).join(' ');
          return <path d={d} fill="none" stroke="#2E75B6" strokeWidth="2"/>;
        })()}
        <text x={85} y={82} textAnchor="middle" fontSize={11} fill="#2E75B6" fontWeight="700">k  (Spring)</text>

        {/* Damper between wall and mass — parallel path */}
        {/* Damper cylinder body */}
        <rect x={30} y={148} width={60} height={20} rx={2} fill="#f0f0f0" stroke="#27ae60" strokeWidth="1.5"/>
        {/* Damper rod */}
        <line x1={90} y1={158} x2={140} y2={158} stroke="#27ae60" strokeWidth="2.5"/>
        {/* Damper piston mark */}
        <line x1={66} y1={148} x2={66} y2={168} stroke="#27ae60" strokeWidth="2"/>
        <text x={55} y={140} textAnchor="middle" fontSize={11} fill="#27ae60" fontWeight="700">b  (Damper)</text>

        {/* Connecting lines */}
        <line x1={30} y1={100} x2={30} y2={158} stroke="#888" strokeWidth="1.2" strokeDasharray="3,2"/>
        <line x1={140} y1={100} x2={140} y2={158} stroke="#888" strokeWidth="1.2" strokeDasharray="3,2"/>

        {/* Mass block */}
        <rect x={140} y={80} width={100} height={100} rx={5} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={190} y={128} textAnchor="middle" fontSize={20} fill="#c0392b" fontWeight="700">M</text>
        <text x={190} y={148} textAnchor="middle" fontSize={11} fill="#7B0D0D">mass (kg)</text>

        {/* Applied force arrow */}
        <line x1={240} y1={130} x2={300} y2={130} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#mt-arr)"/>
        <text x={318} y={126} fontSize={13} fill="#c0392b" fontWeight="700">f(t)</text>
        <text x={256} y={120} fontSize={11} fill="#555">Applied</text>
        <text x={256} y={112} fontSize={11} fill="#555">force</text>

        {/* Displacement arrow below mass */}
        <line x1={140} y1={210} x2={240} y2={210} stroke="#e07b00" strokeWidth="2" markerEnd="url(#mt-arr)"/>
        <text x={190} y={228} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">x(t)</text>
        <text x={190} y={242} textAnchor="middle" fontSize={10} fill="#555">displacement</text>

        {/* Ground rollers */}
        {[150,170,190,210,220].map(xr=>(
          <circle key={xr} cx={xr} cy={185} r={5} fill="#ddd" stroke="#aaa" strokeWidth="1"/>
        ))}
        <line x1={100} y1={192} x2={280} y2={192} stroke="#aaa" strokeWidth="1.5"/>

        {/* Equation panel inside left */}
        <rect x={20} y={250} width={410} height={70} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={30} y={268} fontSize={12} fill="#7B0D0D" fontWeight="700">Newton's 2nd Law → Equation of Motion:</text>
        <text x={30} y={288} fontSize={13} fill="#1a1a2e" fontFamily="'Courier New',monospace">M·ẍ(t) + b·ẋ(t) + k·x(t) = f(t)</text>
        <text x={30} y={308} fontSize={11} fill="#555">Laplace:  (Ms² + bs + k)X(s) = F(s)</text>

        {/* ── RIGHT PANEL: Transfer function & block diagram ── */}
        <rect x={455} y={10} width={435} height={320} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={672} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Transfer Function &amp; Free Body Diagram</text>

        {/* TF derivation steps */}
        {[
          { y:52,  txt:"Sum of forces (Newton's 2nd Law):", bold:true },
          { y:70,  txt:"ΣF = M·ẍ  →  f(t) − k·x − b·ẋ = M·ẍ",  mono:true },
          { y:92,  txt:"Take Laplace transform (zero ICs):", bold:true },
          { y:110, txt:"F(s) − kX(s) − bsX(s) = Ms²X(s)",        mono:true },
          { y:132, txt:"Transfer Function G(s) = X(s)/F(s):", bold:true },
        ].map(({y,txt,bold,mono},i)=>(
          <text key={i} x={465} y={y} fontSize={bold?12:11}
            fontWeight={bold?'700':'400'} fill={bold?'#7B0D0D':'#333'}
            fontFamily={mono?"'Courier New',monospace":"'Georgia',serif"}>{txt}</text>
        ))}

        {/* TF box */}
        <rect x={465} y={140} width={415} height={52} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2"/>
        <text x={672} y={162} textAnchor="middle" fontSize={14} fill="#2E75B6" fontWeight="700" fontFamily="'Courier New',monospace">G(s) = X(s) / F(s) =          1</text>
        <line x1={650} y1={168} x2={875} y2={168} stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={762} y={183} textAnchor="middle" fontSize={14} fill="#2E75B6" fontWeight="700" fontFamily="'Courier New',monospace">Ms² + bs + k</text>

        {/* Natural freq & damping annotations */}
        {[
          { y:215, c:"#e07b00", t:"Natural frequency:  ωₙ = √(k/M)  rad/s" },
          { y:233, c:"#9b59b6", t:"Damping ratio:       ζ = b / (2√(kM))" },
          { y:251, c:"#27ae60", t:"Standard form:  G(s) = ωₙ² / (s² + 2ζωₙs + ωₙ²)" },
        ].map(({y,c,t},i)=>(
          <text key={i} x={465} y={y} fontSize={11} fill={c} fontFamily="'Courier New',monospace">{t}</text>
        ))}

        {/* Three-element parameter table */}
        {[
          { label:'M — mass',    unit:'kg',   role:'Stores kinetic energy (½Mv²)',      fill:'#FEF0F0', bc:'#c0392b' },
          { label:'b — damping', unit:'N·s/m',role:'Dissipates energy (friction/drag)', fill:'#f0fff4', bc:'#27ae60' },
          { label:'k — stiffness',unit:'N/m', role:'Stores potential energy (½kx²)',    fill:'#eaf3ff', bc:'#2E75B6' },
        ].map(({label,unit,role,fill,bc},i)=>(
          <g key={i}>
            <rect x={465} y={263+i*20} width={415} height={19} rx={2} fill={fill} stroke={bc} strokeWidth="0.8"/>
            <text x={470} y={276+i*20} fontSize={10} fill="#333" fontWeight="700">{label}</text>
            <text x={590} y={276+i*20} fontSize={10} fill="#555">[{unit}]</text>
            <text x={660} y={276+i*20} fontSize={10} fill="#444">{role}</text>
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The mass-spring-damper is the canonical mechanical translational model. Applying Newton's second law yields a 2nd-order ODE. Taking the Laplace transform (zero initial conditions) gives the transfer function G(s) = 1/(Ms²+bs+k), whose poles determine the natural frequency ωₙ and damping ratio ζ of the system.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2: Mechanical Rotational System
// ─────────────────────────────────────────────────────────────────
function DiagramMechanicalRotational() {
  const W = 900, H = 300;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mechanical Rotational System — Inertia-Spring-Damper</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: Schematic */}
        <rect x={10} y={10} width={400} height={280} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={210} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Rotational Schematic</text>

        {/* Fixed support */}
        {[0,1,2,3,4].map(i=>(
          <line key={i} x1={50+i*18} y1={250} x2={58+i*18} y2={265} stroke="#888" strokeWidth="1.2"/>
        ))}
        <line x1={50} y1={250} x2={122} y2={250} stroke="#333" strokeWidth="2.5"/>
        <line x1={86} y1={200} x2={86} y2={250} stroke="#333" strokeWidth="2"/>

        {/* Rotational spring (arc zigzag) */}
        {(() => {
          const cx=170, cy=155, r=40;
          const pts = [];
          for(let a=-150; a<=30; a+=10){
            const rad = a*Math.PI/180;
            const rr = r + (Math.sin((a+150)*Math.PI/30)*10*(a%20<10?1:-1));
            pts.push([cx+rr*Math.cos(rad), cy+rr*Math.sin(rad)]);
          }
          return <path d={pts.map((p,i)=>`${i===0?'M':'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')}
            fill="none" stroke="#2E75B6" strokeWidth="2"/>;
        })()}
        <text x={118} y={120} fontSize={11} fill="#2E75B6" fontWeight="700">Kθ (torsional</text>
        <text x={118} y={134} fontSize={11} fill="#2E75B6" fontWeight="700">spring)</text>

        {/* Disk (inertia J) */}
        <circle cx={250} cy={155} r={50} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <circle cx={250} cy={155} r={6}  fill="#c0392b"/>
        <text x={250} y={150} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">J</text>
        <text x={250} y={167} textAnchor="middle" fontSize={10} fill="#7B0D0D">Inertia</text>

        {/* Damper arc */}
        <path d="M 295,130 Q 340,80 330,155" fill="none" stroke="#27ae60" strokeWidth="2.5" strokeDasharray="6,3"/>
        <text x={342} y={115} fontSize={11} fill="#27ae60" fontWeight="700">b (viscous</text>
        <text x={342} y={129} fontSize={11} fill="#27ae60" fontWeight="700">damping)</text>

        {/* Torque arrow (arc around disk) */}
        <path d="M 250,100 A 55,55 0 0 1 305,155" fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#rot-arr)"/>
        <defs>
          <marker id="rot-arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#e07b00"/>
          </marker>
        </defs>
        <text x={262} y={95} fontSize={12} fill="#e07b00" fontWeight="700">τ(t)</text>

        {/* θ label */}
        <text x={220} y={215} fontSize={12} fill="#9b59b6" fontWeight="700">θ(t) — angular</text>
        <text x={220} y={230} fontSize={12} fill="#9b59b6" fontWeight="700">displacement</text>

        {/* Equation */}
        <rect x={18} y={228} width={384} height={54} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={28} y={245} fontSize={11} fill="#7B0D0D" fontWeight="700">Equation of Motion (torque balance):</text>
        <text x={28} y={263} fontSize={12} fill="#1a1a2e" fontFamily="'Courier New',monospace">J·θ̈(t) + b·θ̇(t) + Kθ·θ(t) = τ(t)</text>
        <text x={28} y={278} fontSize={11} fill="#555">Laplace:  (Js² + bs + Kθ)Θ(s) = T(s)</text>

        {/* RIGHT: Translational ↔ Rotational analogy */}
        <rect x={425} y={10} width={465} height={280} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={657} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Translational ↔ Rotational Analogy</text>

        {/* Header */}
        <rect x={433} y={38} width={220} height={26} rx={2} fill="#c0392b"/>
        <text x={543} y={56} textAnchor="middle" fontSize={11} fill="#fff" fontWeight="700">Translational</text>
        <rect x={660} y={38} width={222} height={26} rx={2} fill="#2E75B6"/>
        <text x={771} y={56} textAnchor="middle" fontSize={11} fill="#fff" fontWeight="700">Rotational</text>

        {[
          ['Force  f(t)  [N]',            'Torque  τ(t)  [N·m]'],
          ['Mass  M  [kg]',               'Moment of inertia  J  [kg·m²]'],
          ['Damping  b  [N·s/m]',         'Rotational damping  b  [N·m·s/rad]'],
          ['Stiffness  k  [N/m]',         'Torsional stiffness  Kθ  [N·m/rad]'],
          ['Displacement  x  [m]',        'Angular displacement  θ  [rad]'],
          ['Velocity  ẋ  [m/s]',          'Angular velocity  ω=θ̇  [rad/s]'],
          ['Acceleration  ẍ  [m/s²]',     'Angular acceleration  α=θ̈  [rad/s²]'],
          ['Mẍ + bẋ + kx = f',           'Jθ̈ + bθ̇ + Kθθ = τ'],
          ['G(s) = 1/(Ms²+bs+k)',         'G(s) = 1/(Js²+bs+Kθ)'],
        ].map(([left, right], i) => {
          const y = 72 + i*22;
          const bg = i%2===0 ? '#fff5f5' : '#fff';
          return (
            <g key={i}>
              <rect x={433} y={y} width={449} height={22} fill={bg}/>
              <line x1={433} y1={y} x2={882} y2={y} stroke="#f0cece" strokeWidth="0.8"/>
              <text x={438} y={y+15} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">{left}</text>
              <text x={665} y={y+15} fontSize={10} fill="#333" fontFamily="'Courier New',monospace">{right}</text>
            </g>
          );
        })}
        <line x1={655} y1={38} x2={655} y2={270} stroke="#f0cece" strokeWidth="1.5"/>
        <rect x={433} y={270} width={449} height={18} rx={2} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={657} y={283} textAnchor="middle" fontSize={10} fill="#9b59b6">Identical mathematical form — same analysis tools apply to both domains</text>
      </svg>
      <div style={S.dCaption}>Rotational systems obey the same 2nd-order ODE structure as translational systems. The direct analogy between force↔torque, mass↔inertia, and stiffness/damping coefficients means every translational analysis method (Laplace, root locus, Bode) applies directly to the rotational domain.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3: Electrical Systems — RLC Circuit
// ─────────────────────────────────────────────────────────────────
function DiagramElectrical() {
  const W = 900, H = 330;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Electrical System Modeling — Series RLC Circuit</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <marker id="elec-arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#333"/>
          </marker>
        </defs>

        {/* LEFT: Circuit schematic */}
        <rect x={10} y={10} width={410} height={310} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={215} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Series RLC Circuit</text>

        {/* Top wire */}
        <line x1={40} y1={70} x2={380} y2={70} stroke="#333" strokeWidth="2"/>
        {/* Bottom wire */}
        <line x1={40} y1={230} x2={380} y2={230} stroke="#333" strokeWidth="2"/>
        {/* Left wire (voltage source) */}
        <line x1={40} y1={70} x2={40} y2={230} stroke="#333" strokeWidth="2"/>
        {/* Right wire */}
        <line x1={380} y1={70} x2={380} y2={230} stroke="#333" strokeWidth="2"/>

        {/* Voltage source (left) */}
        <circle cx={40} cy={150} r={22} fill="#f0fff4" stroke="#27ae60" strokeWidth="2"/>
        <text x={40} y={147} textAnchor="middle" fontSize={13} fill="#27ae60" fontWeight="700">~</text>
        <text x={40} y={163} textAnchor="middle" fontSize={10} fill="#27ae60">vᵢ(t)</text>
        <text x={5}  y={126} fontSize={10} fill="#27ae60">+</text>
        <text x={5}  y={178} fontSize={10} fill="#27ae60">−</text>

        {/* Resistor (top, left portion) — rectangle symbol */}
        <rect x={80} y={58} width={70} height={24} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
        <text x={115} y={74} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">R</text>
        <text x={115} y={50} textAnchor="middle" fontSize={10} fill="#e07b00">Resistor</text>

        {/* Inductor (top, middle) — series of bumps */}
        {(() => {
          const bumps = 4, x0=190, y0=70, bw=14;
          const paths = [];
          for(let i=0;i<bumps;i++){
            const cx = x0 + i*bw + bw/2;
            paths.push(`M ${x0+i*bw},${y0} A ${bw/2},${bw/2} 0 0 1 ${x0+(i+1)*bw},${y0}`);
          }
          return <>
            {paths.map((d,i)=><path key={i} d={d} fill="none" stroke="#2E75B6" strokeWidth="2"/>)}
            <text x={x0+bumps*bw/2} y={50} textAnchor="middle" fontSize={10} fill="#2E75B6">Inductor</text>
            <text x={x0+bumps*bw/2} y={93} textAnchor="middle" fontSize={12} fill="#2E75B6" fontWeight="700">L</text>
          </>;
        })()}

        {/* Capacitor (right vertical branch) */}
        <line x1={380} y1={70}  x2={380} y2={128} stroke="#333" strokeWidth="2"/>
        <line x1={355} y1={130} x2={405} y2={130} stroke="#9b59b6" strokeWidth="3"/>
        <line x1={355} y1={142} x2={405} y2={142} stroke="#9b59b6" strokeWidth="3"/>
        <line x1={380} y1={144} x2={380} y2={230} stroke="#333" strokeWidth="2"/>
        <text x={415} y={140} fontSize={12} fill="#9b59b6" fontWeight="700">C</text>
        <text x={415} y={155} fontSize={10} fill="#9b59b6">Capacitor</text>

        {/* Output voltage label */}
        <text x={330} y={175} fontSize={11} fill="#c0392b" fontWeight="700">v₀(t)</text>
        <line x1={350} y1={135} x2={350} y2={165} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>
        <line x1={350} y1={165} x2={380} y2={165} stroke="#c0392b" strokeWidth="1.5"/>

        {/* Current arrow */}
        <line x1={270} y1={70} x2={310} y2={70} stroke="#c0392b" strokeWidth="2" markerEnd="url(#elec-arr)"/>
        <text x={285} y={62} fontSize={11} fill="#c0392b" fontWeight="700">i(t)</text>

        {/* KVL equation */}
        <rect x={18} y={250} width={392} height={60} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={28} y={267} fontSize={11} fill="#7B0D0D" fontWeight="700">KVL around loop:</text>
        <text x={28} y={284} fontSize={12} fill="#333" fontFamily="'Courier New',monospace">L·di/dt + R·i + (1/C)∫i dt = vᵢ(t)</text>
        <text x={28} y={302} fontSize={11} fill="#555" fontFamily="'Courier New',monospace">Laplace: (Ls + R + 1/Cs)I(s) = Vᵢ(s)</text>

        {/* RIGHT: Transfer functions & impedance */}
        <rect x={428} y={10} width={462} height={310} rx={6} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={659} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Impedance, Transfer Functions &amp; Analogies</text>

        {/* Impedance table */}
        <text x={438} y={52} fontSize={12} fill="#7B0D0D" fontWeight="700">Impedance in s-domain (Z = V/I):</text>
        {[
          { el:'Resistor R',   z:'Z_R = R',        c:'#e07b00' },
          { el:'Inductor L',   z:'Z_L = Ls',       c:'#2E75B6' },
          { el:'Capacitor C',  z:'Z_C = 1/(Cs)',   c:'#9b59b6' },
          { el:'Series RLC',   z:'Z = R + Ls + 1/(Cs)', c:'#333' },
        ].map(({el,z,c},i)=>(
          <g key={i}>
            <rect x={436} y={58+i*22} width={448} height={21} rx={2} fill={i%2===0?'#fff5f5':'#fff'}/>
            <text x={442} y={73+i*22} fontSize={11} fill={c} fontWeight="700">{el}</text>
            <text x={600} y={73+i*22} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">{z}</text>
          </g>
        ))}

        {/* TF: current */}
        <text x={438} y={152} fontSize={11} fill="#7B0D0D" fontWeight="700">Transfer Function — Current output:</text>
        <rect x={436} y={158} width={448} height={36} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={660} y={173} textAnchor="middle" fontSize={12} fill="#2E75B6" fontFamily="'Courier New',monospace">I(s)/Vᵢ(s) = Cs / (LCs² + RCs + 1)</text>
        <text x={660} y={188} textAnchor="middle" fontSize={11} fill="#2E75B6" fontFamily="'Courier New',monospace">= (1/L)s / (s² + (R/L)s + 1/(LC))</text>

        {/* TF: voltage across cap */}
        <text x={438} y={210} fontSize={11} fill="#7B0D0D" fontWeight="700">Transfer Function — Voltage across C:</text>
        <rect x={436} y={216} width={448} height={26} rx={3} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={660} y={233} textAnchor="middle" fontSize={12} fill="#9b59b6" fontFamily="'Courier New',monospace">V₀(s)/Vᵢ(s) = 1/(LCs² + RCs + 1)</text>

        {/* Electrical–Mechanical analogy mini-table */}
        <text x={438} y={258} fontSize={11} fill="#7B0D0D" fontWeight="700">Force-Voltage Analogy:</text>
        <rect x={436} y={262} width={448} height={50} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        {[
          ['Voltage  v(t)','Force  f(t)'],
          ['Current  i(t)','Velocity  ẋ(t)'],
          ['Inductance  L','Mass  M'],
          ['Resistance  R','Damping  b'],
          ['1/Capacitance  1/C','Stiffness  k'],
        ].slice(0,3).map(([e,m],i)=>(
          <g key={i}>
            <text x={442} y={278+i*14} fontSize={10} fill="#e07b00">{e}</text>
            <text x={620} y={278+i*14} fontSize={10} fill="#2E75B6">↔  {m}</text>
          </g>
        ))}
        <text x={750} y={288} fontSize={10} fill="#555">…(full table below)</text>
      </svg>
      <div style={S.dCaption}>In electrical circuits, Kirchhoff's Voltage Law (KVL) or Current Law (KCL) provide the governing equations. Working in the Laplace domain with impedances Z(s) converts circuit analysis to algebraic operations. The force-voltage analogy shows that RLC circuits and mass-spring-damper systems share identical mathematical models — the same transfer function form, poles, and frequency response.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4: DC Motor Electromechanical Model
// ─────────────────────────────────────────────────────────────────
function DiagramDCMotor() {
  const W = 900, H = 350;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Electromechanical System — Armature-Controlled DC Motor</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <marker id="dc-arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#333"/>
          </marker>
          <marker id="dc-red" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#c0392b"/>
          </marker>
          <marker id="dc-blue" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#2E75B6"/>
          </marker>
          <marker id="dc-grn" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* ── Electrical side ── */}
        <rect x={10} y={10} width={380} height={200} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={200} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Electrical (Armature Circuit)</text>

        {/* Source */}
        <circle cx={40} cy={120} r={20} fill="#f0fff4" stroke="#27ae60" strokeWidth="2"/>
        <text x={40} y={117} textAnchor="middle" fontSize={12} fill="#27ae60" fontWeight="700">~</text>
        <text x={40} y={131} textAnchor="middle" fontSize={9} fill="#27ae60">Va(s)</text>

        {/* Top wire */}
        <line x1={40} y1={100} x2={40} y2={65} stroke="#333" strokeWidth="1.8"/>
        <line x1={40} y1={65}  x2={330} y2={65} stroke="#333" strokeWidth="1.8"/>

        {/* Ra */}
        <rect x={70} y={53} width={60} height={24} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.8"/>
        <text x={100} y={69} textAnchor="middle" fontSize={11} fill="#e07b00" fontWeight="700">Ra</text>
        <text x={100} y={48} textAnchor="middle" fontSize={9}  fill="#e07b00">Armature resistance</text>

        {/* La (inductor bumps) */}
        {[0,1,2,3].map(i=>(
          <path key={i} d={`M ${170+i*16},65 A 8,8 0 0 1 ${186+i*16},65`} fill="none" stroke="#2E75B6" strokeWidth="2"/>
        ))}
        <text x={202} y={48} textAnchor="middle" fontSize={9}  fill="#2E75B6">Armature inductance</text>
        <text x={202} y={90} textAnchor="middle" fontSize={11} fill="#2E75B6" fontWeight="700">La</text>

        {/* Back-EMF source */}
        <circle cx={310} cy={120} r={20} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={310} y={117} textAnchor="middle" fontSize={9}  fill="#c0392b" fontWeight="700">Eb</text>
        <text x={310} y={130} textAnchor="middle" fontSize={8}  fill="#c0392b">back-EMF</text>
        <text x={285} y={102} fontSize={9} fill="#c0392b">+</text>
        <text x={285} y={140} fontSize={9} fill="#c0392b">−</text>

        {/* Bottom wire */}
        <line x1={40}  y1={140} x2={40}  y2={175} stroke="#333" strokeWidth="1.8"/>
        <line x1={40}  y1={175} x2={330} y2={175} stroke="#333" strokeWidth="1.8"/>
        <line x1={330} y1={100} x2={330} y2={175} stroke="#333" strokeWidth="1.8"/>

        {/* Current arrow */}
        <line x1={150} y1={65} x2={168} y2={65} stroke="#c0392b" strokeWidth="2" markerEnd="url(#dc-red)"/>
        <text x={155} y={58} fontSize={10} fill="#c0392b" fontWeight="700">ia(t)</text>

        {/* KVL equation */}
        <rect x={18} y={195} width={374} height={42} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={28} y={212} fontSize={11} fill="#7B0D0D" fontWeight="700">KVL: Va = La·(dia/dt) + Ra·ia + Eb</text>
        <text x={28} y={230} fontSize={10} fill="#555" fontFamily="'Courier New',monospace">Laplace: Va(s) = (Las + Ra)Ia(s) + Eb(s)</text>

        {/* ── Mechanical side ── */}
        <rect x={400} y={10} width={290} height={200} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={545} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Mechanical (Rotor)</text>

        {/* Motor disk */}
        <circle cx={500} cy={120} r={55} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <circle cx={500} cy={120} r={10} fill="#c0392b"/>
        <text x={500} y={115} textAnchor="middle" fontSize={13} fill="#c0392b" fontWeight="700">J</text>
        <text x={500} y={132} textAnchor="middle" fontSize={9}  fill="#7B0D0D">Rotor inertia</text>

        {/* Damper on shaft */}
        <rect x={555} y={110} width={40} height={20} rx={2} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={575} y={124} textAnchor="middle" fontSize={9} fill="#27ae60" fontWeight="700">bm</text>
        <text x={605} y={116} fontSize={8} fill="#27ae60">viscous</text>
        <text x={605} y={128} fontSize={8} fill="#27ae60">friction</text>

        {/* Output shaft */}
        <line x1={595} y1={120} x2={670} y2={120} stroke="#333" strokeWidth="3"/>
        <text x={655} y={113} fontSize={10} fill="#27ae60" fontWeight="700">ω(s)</text>
        <text x={655} y={128} fontSize={9}  fill="#555">angular vel.</text>

        {/* Torque arrow */}
        <path d="M 500,62 A 58,58 0 0 1 558,120" fill="none" stroke="#e07b00" strokeWidth="2" markerEnd="url(#dc-arr)"/>
        <text x={548} y={68} fontSize={10} fill="#e07b00" fontWeight="700">Tm = Kt·ia</text>

        {/* Mechanical equation */}
        <rect x={408} y={195} width={274} height={42} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={418} y={212} fontSize={11} fill="#7B0D0D" fontWeight="700">Newton: Tm = J·(dω/dt) + bm·ω</text>
        <text x={418} y={230} fontSize={10} fill="#555" fontFamily="'Courier New',monospace">Laplace: KtIa(s) = (Js + bm)Ω(s)</text>

        {/* ── Coupling + TF panel ── */}
        <rect x={700} y={10} width={190} height={200} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={795} y={28} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Coupling Constants</text>
        {[
          { y:48,  c:'#c0392b', t:'Kt — motor torque', s:'constant  [N·m/A]' },
          { y:82,  c:'#2E75B6', t:'Kb — back-EMF', s:'constant  [V·s/rad]' },
          { y:116, c:'#27ae60', t:'Eb = Kb·ω', s:'back-EMF voltage' },
          { y:150, c:'#e07b00', t:'Tm = Kt·ia', s:'motor torque' },
        ].map(({y,c,t,s},i)=>(
          <g key={i}>
            <rect x={708} y={y} width={174} height={30} rx={2} fill={i%2===0?'#fff5f5':'#eaf3ff'} stroke={c} strokeWidth="0.8"/>
            <text x={716} y={y+13} fontSize={10} fill={c} fontWeight="700">{t}</text>
            <text x={716} y={y+25} fontSize={9}  fill="#555">{s}</text>
          </g>
        ))}
        <text x={795} y={188} textAnchor="middle" fontSize={9} fill="#555">Kt = Kb  (SI units)</text>

        {/* ── Overall TF block ── */}
        <rect x={10} y={248} width={880} height={90} rx={6} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2"/>
        <text x={450} y={267} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Overall Transfer Function — Armature-Controlled DC Motor</text>

        <text x={30}  y={288} fontSize={12} fill="#2E75B6" fontFamily="'Courier New',monospace">Ω(s)/Va(s) =</text>
        <text x={155} y={288} fontSize={14} fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">Kt</text>
        <line x1={152} y1={292} x2={340} y2={292} stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={152} y={308} fontSize={12} fill="#2E75B6" fontFamily="'Courier New',monospace">(Las+Ra)(Js+bm) + KtKb</text>

        <text x={360} y={295} fontSize={11} fill="#555">≈  (if La≈0)</text>
        <text x={470} y={288} fontSize={14} fill="#c0392b" fontFamily="'Courier New',monospace" fontWeight="700">Kt / Ra</text>
        <line x1={468} y1={292} x2={650} y2={292} stroke="#c0392b" strokeWidth="1.5"/>
        <text x={468} y={308} fontSize={12} fill="#c0392b" fontFamily="'Courier New',monospace">s(Js + bm + KtKb/Ra)</text>

        <text x={665} y={290} fontSize={10} fill="#555">Two poles: s=0 (integrator)</text>
        <text x={665} y={305} fontSize={10} fill="#555">and s = −(bm+KtKb/Ra)/J</text>
        <text x={665} y={320} fontSize={10} fill="#9b59b6">→ position output: θ(s)/Va(s)</text>
        <text x={665} y={333} fontSize={10} fill="#9b59b6">adds another 1/s (integrator)</text>
      </svg>
      <div style={S.dCaption}>The armature-controlled DC motor couples an electrical circuit (governed by KVL) to a mechanical rotor (governed by Newton's law) through the motor torque constant Kt and back-EMF constant Kb. Eliminating the intermediate variable Ia(s) yields a transfer function with a pole at the origin (integrating action) — meaning the motor naturally integrates velocity to give position.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5: Thermal & Fluid System Analogy
// ─────────────────────────────────────────────────────────────────
function DiagramThermalFluid() {
  const W = 900, H = 320;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Thermal &amp; Fluid System Models — First-Order Analogies</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* THERMAL panel */}
        <rect x={10} y={10} width={415} height={300} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={218} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Thermal System (Heated Room)</text>

        {/* Room box */}
        <rect x={100} y={60} width={160} height={120} rx={5} fill="#fff5e6" stroke="#e07b00" strokeWidth="2"/>
        <text x={180} y={118} textAnchor="middle" fontSize={14} fill="#e07b00" fontWeight="700">T(t)</text>
        <text x={180} y={135} textAnchor="middle" fontSize={10} fill="#555">room temp</text>

        {/* Heat input q(t) */}
        {[0,1,2,3,4,5].map(i=>(
          <line key={i} x1={50+i*5} y1={90+i*8} x2={98} y2={90+i*8} stroke="#c0392b" strokeWidth={2-i*0.2} opacity={1-i*0.12}/>
        ))}
        <text x={52} y={85} fontSize={10} fill="#c0392b" fontWeight="700">q(t)</text>
        <text x={30} y={98} fontSize={9}  fill="#c0392b">Heat</text>
        <text x={30} y={110} fontSize={9} fill="#c0392b">input</text>

        {/* Heat loss through wall */}
        <rect x={262} y={88} width={30} height={44} rx={2} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={277} y={107} textAnchor="middle" fontSize={9} fill="#2E75B6" fontWeight="700">R</text>
        <text x={277} y={119} textAnchor="middle" fontSize={9} fill="#2E75B6" fontWeight="700">th</text>
        {[0,1,2,3,4].map(i=>(
          <line key={i} x1={292} y1={92+i*8} x2={340} y2={92+i*8} stroke="#2E75B6" strokeWidth="1.2" opacity={0.6+i*0.08}/>
        ))}
        <text x={350} y={105} fontSize={9}  fill="#2E75B6">Thermal</text>
        <text x={350} y={117} fontSize={9}  fill="#2E75B6">resistance</text>

        {/* Ambient temperature */}
        <text x={350} y={140} fontSize={11} fill="#27ae60" fontWeight="700">T∞</text>
        <text x={350} y={154} fontSize={9}  fill="#555">ambient</text>

        {/* Capacitance label */}
        <text x={180} y={78} textAnchor="middle" fontSize={9} fill="#9b59b6">Thermal capacitance Cth</text>

        {/* Equations */}
        <rect x={18} y={200} width={396} height={100} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={28} y={218} fontSize={11} fill="#7B0D0D" fontWeight="700">Energy balance:</text>
        <text x={28} y={235} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">Cth·dT/dt = q(t) − (T−T∞)/Rth</text>
        <text x={28} y={253} fontSize={11} fill="#7B0D0D" fontWeight="700">Transfer function (θ=T−T∞):</text>
        <text x={28} y={270} fontSize={11} fill="#2E75B6" fontFamily="'Courier New',monospace">Θ(s)/Q(s) = Rth / (RthCth·s + 1)</text>
        <text x={28} y={288} fontSize={11} fill="#555" fontFamily="'Courier New',monospace">Time const. τ = RthCth  |  DC gain = Rth</text>

        {/* FLUID panel */}
        <rect x={440} y={10} width={450} height={300} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={665} y={30} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Fluid (Liquid-Level) System</text>

        {/* Tank */}
        <rect x={560} y={55} width={120} height={150} rx={4} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2"/>
        {/* Fluid fill */}
        <rect x={562} y={115} width={116} height={88} rx={2} fill="#bde0f7" opacity="0.7"/>
        <text x={620} y={175} textAnchor="middle" fontSize={13} fill="#2E75B6" fontWeight="700">h(t)</text>
        <text x={620} y={190} textAnchor="middle" fontSize={9}  fill="#555">fluid height</text>

        {/* Inflow */}
        <line x1={580} y1={20} x2={580} y2={54} stroke="#2E75B6" strokeWidth="3"/>
        {[0,1,2].map(i=>(
          <polygon key={i} points={`${574+i*3},${38-i*5} ${586-i*3},${38-i*5} ${580},${52-i*5}`} fill="#2E75B6" opacity={0.4+i*0.3}/>
        ))}
        <text x={555} y={18} fontSize={10} fill="#2E75B6" fontWeight="700">qᵢ(t)</text>
        <text x={538} y={30} fontSize={9} fill="#555">Inflow</text>

        {/* Outflow through valve */}
        <line x1={680} y1={175} x2={760} y2={175} stroke="#27ae60" strokeWidth="2.5"/>
        <rect x={740} y={168} width={20} height={14} rx={2} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={745} y={178} fontSize={8} fill="#27ae60" fontWeight="700">R</text>
        <line x1={760} y1={175} x2={820} y2={175} stroke="#27ae60" strokeWidth="2.5"/>
        {[0,1,2].map(i=>(
          <polygon key={i} points={`${810+i*4},${169} ${810+i*4},${181} ${824+i*4},${175}`} fill="#27ae60" opacity={0.4+i*0.3}/>
        ))}
        <text x={785} y={162} fontSize={10} fill="#27ae60" fontWeight="700">q₀(t)</text>
        <text x={785} y={193} fontSize={9}  fill="#555">Outflow</text>
        <text x={752} y={158} fontSize={9}  fill="#27ae60">Valve</text>

        {/* Cross-section area label */}
        <line x1={560} y1={48} x2={680} y2={48} stroke="#9b59b6" strokeWidth="1.2" strokeDasharray="4,3"/>
        <text x={620} y={44} textAnchor="middle" fontSize={9} fill="#9b59b6">Area A</text>

        {/* Equations */}
        <rect x={448} y={225} width={434} height={75} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={458} y={243} fontSize={11} fill="#7B0D0D" fontWeight="700">Continuity equation (linear valve: q₀ = h/Rf):</text>
        <text x={458} y={260} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">A·dh/dt = qᵢ(t) − h(t)/Rf</text>
        <text x={458} y={278} fontSize={11} fill="#7B0D0D" fontWeight="700">Transfer function:</text>
        <text x={458} y={295} fontSize={11} fill="#2E75B6" fontFamily="'Courier New',monospace">H(s)/Qᵢ(s) = Rf / (ARf·s + 1) = Rf / (τs + 1)  τ=ARf</text>
      </svg>
      <div style={S.dCaption}>Thermal and fluid systems both yield first-order transfer functions of the form K/(τs+1). The thermal time constant τ = RthCth governs how quickly a body heats or cools; the fluid time constant τ = ARf governs how quickly a tank fills or drains. These models are directly analogous to a simple RC electrical circuit.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6: State-Space Representation
// ─────────────────────────────────────────────────────────────────
function DiagramStateSpace() {
  const W = 900, H = 360;
  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>State-Space Representation — Signal Flow &amp; Matrix Form</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <marker id="ss-arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#333"/>
          </marker>
          <marker id="ss-red" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#c0392b"/>
          </marker>
          <marker id="ss-blue" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#2E75B6"/>
          </marker>
          <marker id="ss-grn" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* ── TOP: Block diagram for ẋ = Ax + Bu, y = Cx + Du ── */}
        <rect x={10} y={10} width={880} height={170} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={450} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">State-Space Block Diagram: ẋ = Ax + Bu  |  y = Cx + Du</text>

        {/* u(t) input */}
        <text x={20} y={100} fontSize={12} fill="#c0392b" fontWeight="700">u(t)</text>
        <line x1={45} y1={97} x2={90} y2={97} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ss-red)"/>

        {/* Summing jxn */}
        <circle cx={108} cy={97} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={108} y={102} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <text x={92} y={94}  fontSize={11} fill="#27ae60" fontWeight="700">+</text>
        <text x={104} y={116} fontSize={11} fill="#27ae60" fontWeight="700">+</text>

        {/* B block */}
        <line x1={45} y1={97} x2={45} y2={145} stroke="#333" strokeWidth="1.5"/>
        <line x1={45} y1={145} x2={108} y2={145} stroke="#333" strokeWidth="1.5" markerEnd="url(#ss-arr)"/>
        <rect x={0}  y={138} width={42} height={22} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1.5"/>
        <text x={21} y={153} textAnchor="middle" fontSize={12} fill="#e07b00" fontWeight="700">B</text>
        <text x={21} y={136} textAnchor="middle" fontSize={9}  fill="#555">input matrix</text>

        {/* ẋ line → integrator */}
        <line x1={126} y1={97} x2={200} y2={97} stroke="#333" strokeWidth="2" markerEnd="url(#ss-arr)"/>
        <text x={155} y={90} fontSize={11} fill="#555">ẋ(t)</text>

        {/* Integrator block (1/s) */}
        <rect x={200} y={77} width={80} height={40} rx={5} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="2"/>
        <text x={240} y={95} textAnchor="middle" fontSize={13} fill="#2E75B6" fontWeight="700">∫</text>
        <text x={240} y={111} textAnchor="middle" fontSize={10} fill="#2E75B6">1/s</text>

        {/* x(t) line */}
        <line x1={280} y1={97} x2={380} y2={97} stroke="#27ae60" strokeWidth="2" markerEnd="url(#ss-grn)"/>
        <text x={315} y={90} fontSize={11} fill="#27ae60">x(t)</text>

        {/* C block */}
        <rect x={380} y={77} width={70} height={40} rx={4} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={415} y={99} textAnchor="middle" fontSize={13} fill="#9b59b6" fontWeight="700">C</text>
        <text x={415} y={68} textAnchor="middle" fontSize={9}  fill="#555">output matrix</text>

        {/* Summing junction for output */}
        <circle cx={530} cy={97} r={18} fill="#FEF0F0" stroke="#c0392b" strokeWidth="2"/>
        <text x={530} y={102} textAnchor="middle" fontSize={16} fill="#c0392b" fontWeight="700">Σ</text>
        <line x1={450} y1={97} x2={508} y2={97} stroke="#333" strokeWidth="2" markerEnd="url(#ss-arr)"/>
        <text x={476} y={90} fontSize={11} fill="#555">Cx</text>

        {/* D (feedthrough) path */}
        <line x1={45}  y1={97}  x2={45} y2={50}  stroke="#333" strokeWidth="1.5"/>
        <line x1={45}  y1={50}  x2={490} y2={50}  stroke="#333" strokeWidth="1.5"/>
        <line x1={490} y1={50}  x2={490} y2={89}  stroke="#333" strokeWidth="1.5" markerEnd="url(#ss-arr)"/>
        <rect x={245} y={38} width={45} height={22} rx={3} fill="#fff5f5" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={267} y={53} textAnchor="middle" fontSize={12} fill="#c0392b" fontWeight="700">D</text>
        <text x={267} y={36} textAnchor="middle" fontSize={9}  fill="#555">feedthrough</text>

        {/* Output y(t) */}
        <line x1={548} y1={97} x2={620} y2={97} stroke="#c0392b" strokeWidth="2" markerEnd="url(#ss-red)"/>
        <text x={590} y={90} fontSize={12} fill="#c0392b" fontWeight="700">y(t)</text>

        {/* A feedback path */}
        <circle cx={330} cy={97} r={4} fill="#333"/>
        <line x1={330} y1={101} x2={330} y2={158} stroke="#c0392b" strokeWidth="1.5"/>
        <line x1={330} y1={158} x2={108} y2={158} stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#ss-red)"/>
        <rect x={248} y={150} width={45} height={22} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5"/>
        <text x={270} y={165} textAnchor="middle" fontSize={13} fill="#c0392b" fontWeight="700">A</text>
        <text x={270} y={148} textAnchor="middle" fontSize={9}  fill="#555">system matrix</text>
        <text x={200} y={158} fontSize={9} fill="#c0392b">Ax(t) feedback</text>

        {/* ── BOTTOM: Matrix form and key concepts ── */}
        <rect x={10} y={192} width={420} height={158} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={220} y={210} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Matrix Equations &amp; Dimensions</text>

        {[
          { y:228, c:'#c0392b', t:'State equation:    ẋ(t) = A·x(t) + B·u(t)' },
          { y:245, c:'#9b59b6', t:'Output equation:   y(t) = C·x(t) + D·u(t)' },
          { y:265, c:'#333',    t:'A: [n×n] system,  B: [n×m] input' },
          { y:281, c:'#333',    t:'C: [p×n] output,  D: [p×m] feedthrough' },
          { y:301, c:'#2E75B6', t:'Transfer function: G(s) = C(sI−A)⁻¹B + D' },
          { y:317, c:'#27ae60', t:'Eigenvalues of A = open-loop poles' },
          { y:335, c:'#e07b00', t:'n = order,  m = inputs,  p = outputs' },
        ].map(({y,c,t},i)=>(
          <text key={i} x={20} y={y} fontSize={11} fill={c} fontFamily={t.includes('=')||t.includes('[')?'Courier New,monospace':'Georgia,serif'}>{t}</text>
        ))}

        {/* Key matrices example */}
        <rect x={440} y={192} width={450} height={158} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={665} y={210} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Example: Mass-Spring-Damper in State-Space</text>

        <text x={450} y={232} fontSize={11} fill="#555">Choose state variables: x₁ = x (position), x₂ = ẋ (velocity)</text>
        <text x={450} y={250} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">ẋ₁ = x₂</text>
        <text x={450} y={266} fontSize={11} fill="#333" fontFamily="'Courier New',monospace">ẋ₂ = −(k/M)x₁ − (b/M)x₂ + (1/M)f</text>

        {/* Matrix brackets */}
        <text x={450} y={292} fontSize={11} fill="#7B0D0D" fontWeight="700">A =</text>
        <rect x={478} y={278} width={100} height={42} rx={2} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.5"/>
        <text x={528} y={295} textAnchor="middle" fontSize={10} fill="#2E75B6" fontFamily="'Courier New',monospace">[ 0      1  ]</text>
        <text x={528} y={312} textAnchor="middle" fontSize={10} fill="#2E75B6" fontFamily="'Courier New',monospace">[-k/M  -b/M]</text>

        <text x={590} y={292} fontSize={11} fill="#7B0D0D" fontWeight="700">B =</text>
        <rect x={616} y={278} width={68} height={42} rx={2} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.5"/>
        <text x={650} y={295} textAnchor="middle" fontSize={10} fill="#27ae60" fontFamily="'Courier New',monospace">[ 0  ]</text>
        <text x={650} y={312} textAnchor="middle" fontSize={10} fill="#27ae60" fontFamily="'Courier New',monospace">[1/M]</text>

        <text x={696} y={292} fontSize={11} fill="#7B0D0D" fontWeight="700">C =</text>
        <rect x={720} y={278} width={85} height={24} rx={2} fill="#f5d0ff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={762} y={294} textAnchor="middle" fontSize={10} fill="#9b59b6" fontFamily="'Courier New',monospace">[1  0]</text>
        <text x={720} y={322} fontSize={10} fill="#555">(position output)</text>

        <text x={450} y={340} fontSize={10} fill="#e07b00" fontFamily="'Courier New',monospace">Poles = eigenvalues of A = roots of s² + (b/M)s + k/M = 0  ✓</text>
      </svg>
      <div style={S.dCaption}>State-space representation models a system as a set of first-order ODEs using state variables. Every nth-order system can be written in this form with n state variables. The four matrices (A, B, C, D) completely characterise the system; the eigenvalues of A are identical to the transfer function poles, and the transfer function is recovered via G(s) = C(sI−A)⁻¹B + D.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7: Linearisation Around an Operating Point
// ─────────────────────────────────────────────────────────────────
function DiagramLinearisation() {
  const W = 900, H = 310;

  // Nonlinear curve: y = tanh(2x) scaled
  const nlPts = [];
  for(let i=0; i<=100; i++){
    const x = -2.5 + i*0.05;
    const y = Math.tanh(1.4*x);
    nlPts.push({ x, y });
  }
  const sx = (x) => 140 + (x+2.5)*70;
  const sy = (y) => 155 - y*90;

  const pathD = nlPts.map((p,i)=>`${i===0?'M':'L'}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`).join(' ');

  // Operating point
  const x0 = 0.5, y0 = Math.tanh(1.4*x0);
  const slope = 1.4*(1-Math.tanh(1.4*x0)**2); // derivative of tanh(1.4x) at x0
  const linD = `M ${sx(x0-1.2)},${sy(y0-slope*1.2)} L ${sx(x0+1.5)},${sy(y0+slope*1.5)}`;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Linearisation — Approximating Nonlinear Systems Around an Operating Point</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* LEFT: Graph */}
        <rect x={10} y={10} width={440} height={290} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={225} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Nonlinear Function vs Linearised Approximation</text>

        {/* Axes */}
        <line x1={80} y1={260} x2={430} y2={260} stroke="#333" strokeWidth="2"/>
        <line x1={80} y1={40}  x2={80}  y2={260} stroke="#333" strokeWidth="2"/>
        <text x={235} y={280} textAnchor="middle" fontSize={11} fill="#333">Input  u</text>
        <text x={28}  y={155} textAnchor="middle" fontSize={11} fill="#333" transform="rotate(-90,28,155)">Output  y</text>

        {/* Grid */}
        {[-2,-1,0,1,2].map(v=>(
          <g key={v}>
            <line x1={sx(v)} y1={40} x2={sx(v)} y2={260} stroke="#f0cece" strokeWidth="1"/>
            <text x={sx(v)} y={272} textAnchor="middle" fontSize={9} fill="#888">{v}</text>
          </g>
        ))}
        {[-1,-0.5,0,0.5,1].map(v=>(
          <line key={v} x1={80} y1={sy(v)} x2={430} y2={sy(v)} stroke="#f0cece" strokeWidth="1"/>
        ))}

        {/* Nonlinear curve */}
        <path d={pathD} fill="none" stroke="#c0392b" strokeWidth="2.5"/>
        <text x={390} y={62} fontSize={11} fill="#c0392b" fontWeight="700">f(u) — nonlinear</text>

        {/* Linearised tangent */}
        <path d={linD} fill="none" stroke="#2E75B6" strokeWidth="2" strokeDasharray="7,4"/>
        <text x={360} y={92} fontSize={11} fill="#2E75B6" fontWeight="700">Δy ≈ K·Δu</text>
        <text x={360} y={106} fontSize={10} fill="#2E75B6">(tangent line)</text>

        {/* Operating point */}
        <circle cx={sx(x0)} cy={sy(y0)} r={7} fill="#e07b00" stroke="#fff" strokeWidth="2"/>
        <line x1={sx(x0)} y1={sy(y0)} x2={sx(x0)} y2={260} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <line x1={80} y1={sy(y0)} x2={sx(x0)} y2={sy(y0)} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="4,3"/>
        <text x={sx(x0)+10} y={sy(y0)-10} fontSize={11} fill="#e07b00" fontWeight="700">Operating point</text>
        <text x={sx(x0)+10} y={sy(y0)+5}  fontSize={10} fill="#e07b00">(u₀, y₀)</text>

        {/* Δu and Δy arrows */}
        <line x1={sx(x0)} y1={sy(y0)} x2={sx(x0+0.8)} y2={sy(y0)} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#lin-arr)"/>
        <text x={sx(x0+0.4)} y={sy(y0)+16} textAnchor="middle" fontSize={10} fill="#27ae60" fontWeight="700">Δu</text>
        <line x1={sx(x0+0.8)} y1={sy(y0)} x2={sx(x0+0.8)} y2={sy(y0+slope*0.8)} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#lin-arr)"/>
        <text x={sx(x0+0.8)+14} y={sy(y0+slope*0.4)} fontSize={10} fill="#27ae60" fontWeight="700">Δy</text>

        <defs>
          <marker id="lin-arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <polygon points="0 0,7 3,0 6" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Valid region shading */}
        <rect x={sx(x0-0.7)} y={40} width={sx(x0+0.7)-sx(x0-0.7)} height={220} fill="#2E75B6" opacity="0.06"/>
        <text x={sx(x0)} y={248} textAnchor="middle" fontSize={9} fill="#2E75B6" opacity="0.8">Valid linear region</text>

        {/* RIGHT: Taylor expansion + procedure */}
        <rect x={460} y={10} width={430} height={290} rx={6} fill="#fff" stroke="#f0cece"/>
        <text x={675} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">Taylor Series Linearisation Procedure</text>

        {[
          { y:48,  c:'#7B0D0D', bold:true,  t:'Taylor expansion about (u₀, y₀):' },
          { y:66,  c:'#c0392b', mono:true,  t:'f(u) ≈ f(u₀) + f\'(u₀)·(u−u₀) + ...' },
          { y:84,  c:'#555',    bold:false, t:'Truncate at 1st order (small perturbation Δu = u−u₀):' },
          { y:101, c:'#2E75B6', mono:true,  t:'Δy ≈ K·Δu      K = df/du|ᵤ₀' },
          { y:122, c:'#7B0D0D', bold:true,  t:'For ODE ẋ = f(x,u) around (x₀,u₀):' },
          { y:140, c:'#c0392b', mono:true,  t:'Δẋ = A·Δx + B·Δu' },
          { y:157, c:'#333',    mono:true,  t:'A = ∂f/∂x|(x₀,u₀)   B = ∂f/∂u|(x₀,u₀)' },
          { y:178, c:'#7B0D0D', bold:true,  t:'Linearisation steps:' },
        ].map(({y,c,bold,mono,t},i)=>(
          <text key={i} x={470} y={y} fontSize={bold?11.5:11}
            fontWeight={bold?'700':'400'} fill={c}
            fontFamily={mono?"'Courier New',monospace":"'Georgia',serif"}>{t}</text>
        ))}

        {[
          '1. Find equilibrium / operating point (set all derivatives = 0)',
          '2. Define perturbation variables: Δx = x−x₀, Δu = u−u₀',
          '3. Compute Jacobian matrices A = ∂f/∂x and B = ∂f/∂u',
          '4. Write linearised model: Δẋ = AΔx + BΔu',
          '5. Apply Laplace transform to get transfer function',
        ].map((t,i)=>(
          <g key={i}>
            <rect x={468} y={186+i*20} width={414} height={18} rx={2} fill={i%2===0?'#fff5f5':'#fff'}/>
            <text x={475} y={199+i*20} fontSize={10} fill="#333">{t}</text>
          </g>
        ))}

        <rect x={468} y={292} width={414} height={0} rx={0}/>

        {/* Warning box */}
        <rect x={468} y={286} width={414} height={14} rx={2} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
        <text x={675} y={297} textAnchor="middle" fontSize={9} fill="#e07b00" fontWeight="700">⚠ Linear model only valid for small perturbations near the operating point</text>
      </svg>
      <div style={S.dCaption}>Most real physical systems are nonlinear (e.g. pendulums, saturating motors, nonlinear valves). Linearisation uses a first-order Taylor expansion around a chosen equilibrium point to produce a linear model valid for small perturbations. The resulting linear model enables all the standard transfer function and state-space analysis tools to be applied.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8: System Analogies Comparison Table (visual)
// ─────────────────────────────────────────────────────────────────
function DiagramAnalogies() {
  const W = 900, H = 280;
  const domains = [
    { name:'Mechanical\nTranslational', color:'#c0392b', bg:'#FEF0F0',
      rows:['Force  f  [N]','Mass  M  [kg]','Damper  b  [N·s/m]','Spring  k  [N/m]','Velocity  ẋ  [m/s]','Displacement  x  [m]'] },
    { name:'Mechanical\nRotational', color:'#2E75B6', bg:'#eaf3ff',
      rows:['Torque  τ  [N·m]','Inertia  J  [kg·m²]','Damper  b  [N·m·s]','Spring  Kθ  [N·m/rad]','Ang. vel.  ω  [rad/s]','Angle  θ  [rad]'] },
    { name:'Electrical\n(Force-Voltage)', color:'#e07b00', bg:'#fff5e6',
      rows:['Voltage  V  [V]','Inductance  L  [H]','Resistance  R  [Ω]','1/Capacitance  1/C','Current  i  [A]','Charge  q  [C]'] },
    { name:'Electrical\n(Force-Current)', color:'#9b59b6', bg:'#f5d0ff',
      rows:['Current  I  [A]','Capacitance  C  [F]','Conductance  1/R','1/Inductance  1/L','Voltage  V  [V]','Flux  λ  [Wb]'] },
    { name:'Thermal', color:'#27ae60', bg:'#f0fff4',
      rows:['Heat flow  q  [W]','— (no exact)','Resistance  Rth  [°C/W]','— (no exact)','Temperature  T  [°C]','Heat energy  Q  [J]'] },
    { name:'Fluid\n(Liquid Level)', color:'#555', bg:'#f8f8f8',
      rows:['Flow rate  q  [m³/s]','— (no exact)','Fluid resist.  Rf','— (no exact)','Pressure  P  [Pa]','Volume  V  [m³]'] },
  ];

  const rowLabels = ['Through variable\n(analogous to force)','Inertance\n(analogous to mass)','Dissipation\n(analogous to damping)','Compliance\n(analogous to spring)','Across variable\n(analogous to velocity)','Integrated across\n(analogous to displ.)'];
  const colW = 128, rowH = 33, leftW = 116;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>System Analogies — Unified Cross-Domain Comparison</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Row labels */}
        {rowLabels.map((lbl,i)=>(
          <g key={i}>
            <rect x={2} y={48+i*rowH} width={leftW-4} height={rowH} fill={i%2===0?'#fff5f5':'#fff'} stroke="#f0cece" strokeWidth="0.5"/>
            {lbl.split('\n').map((l,j)=>(
              <text key={j} x={leftW/2} y={58+i*rowH+j*13} textAnchor="middle" fontSize={8.5} fill="#7B0D0D" fontWeight="700">{l}</text>
            ))}
          </g>
        ))}

        {/* Domain columns */}
        {domains.map((dom,ci)=>{
          const x = leftW + ci*colW;
          return (
            <g key={ci}>
              {/* Header */}
              <rect x={x} y={10} width={colW} height={38} rx={3} fill={dom.color}/>
              {dom.name.split('\n').map((l,j)=>(
                <text key={j} x={x+colW/2} y={26+j*14} textAnchor="middle" fontSize={10} fill="#fff" fontWeight="700">{l}</text>
              ))}
              {/* Rows */}
              {dom.rows.map((val,ri)=>(
                <g key={ri}>
                  <rect x={x} y={48+ri*rowH} width={colW} height={rowH} fill={dom.bg} stroke="#f0cece" strokeWidth="0.5"/>
                  <text x={x+colW/2} y={64+ri*rowH} textAnchor="middle" fontSize={8.5} fill="#333" fontFamily="'Courier New',monospace">{val}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Bottom: governing equation */}
        <rect x={2} y={246} width={W-4} height={28} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
        <text x={W/2} y={256} textAnchor="middle" fontSize={10} fill="#7B0D0D" fontWeight="700">Universal 2nd-order ODE (all domains):</text>
        <text x={W/2} y={270} textAnchor="middle" fontSize={10} fill="#333" fontFamily="'Courier New',monospace">[Inertance]·ẍ + [Dissipation]·ẋ + [1/Compliance]·x = [Through variable]   →   Transfer Function: G(s) = 1 / (Is² + Ds + 1/C)</text>
      </svg>
      <div style={S.dCaption}>All physical domains obey the same mathematical structure. The cross-domain analogy allows engineers to: (1) model an unfamiliar domain using familiar techniques, (2) build electrical analogue computers of mechanical systems, and (3) immediately identify natural frequency (ωₙ = √(1/IC)) and damping ratio (ζ = D/(2√(I/C))) from any domain's parameters.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function SystemModeling() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — System Modeling</h1>
      <p style={S.p}>
        System modeling is the process of translating a physical system — mechanical, electrical, thermal, fluid, or electromechanical — into a mathematical form that can be analysed and controlled. The goal is a transfer function G(s) or a state-space model (A, B, C, D) that accurately captures the input-output dynamics. Every model starts with a fundamental physical law: Newton's second law, Kirchhoff's laws, conservation of energy or mass. The Laplace transform then converts these differential equations into algebraic relationships, enabling the full toolkit of control theory to be applied.
      </p>

      <InfoBox title="Core Modeling Principles" items={[
        "Every model begins with a physical law: Newton (mechanical), KVL/KCL (electrical), energy balance (thermal), continuity (fluid)",
        "The Laplace transform converts ODEs into algebraic transfer functions: G(s) = Y(s)/U(s), valid for LTI systems with zero ICs",
        "System order = highest derivative in the ODE = number of energy-storage elements",
        "All physical domains share the same 2nd-order ODE structure via cross-domain analogies",
        "State-space form ẋ = Ax + Bu, y = Cx + Du represents any order system as coupled 1st-order ODEs",
        "Nonlinear systems must be linearised around an operating point before standard linear control tools apply",
        "Electromechanical systems couple two domains; the DC motor is the most important example",
        "Model simplification (e.g. ignoring La in a motor) must be justified and the error quantified",
      ]}/>

      <h2 style={S.h2}>Mechanical Translational Systems</h2>
      <p style={S.p}>
        Translational mechanical systems are governed by Newton's second law: the net force on a mass equals its mass times acceleration. Three passive elements — mass M, viscous damper b, and spring k — store kinetic energy, dissipate energy, and store potential energy respectively. Drawing a free-body diagram and summing forces gives the equation of motion directly.
      </p>
      <DiagramMechanicalTranslational/>
      <Table2 rows={[
        ["Newton's 2nd Law","ΣF = Ma   →   M·ẍ + b·ẋ + k·x = f(t)\nApply to each free mass in the system"],
        ["Inertia element (M)","Force: fM = M·ẍ  →  FM(s) = Ms²X(s)\nStores kinetic energy: KE = ½Mv²\nImpedance: ZM = Ms²"],
        ["Viscous damper (b)","Force: fb = b·ẋ  →  FB(s) = bsX(s)\nDissipates energy proportional to velocity\nImpedance: Zb = bs"],
        ["Spring (k)","Force: fk = k·x  →  FK(s) = kX(s)\nStores potential energy: PE = ½kx²\nImpedance: Zk = k"],
        ["Transfer function","G(s) = X(s)/F(s) = 1/(Ms² + bs + k)\nCharacteristic equation: Ms² + bs + k = 0\nPoles: s = [−b ± √(b²−4Mk)] / 2M"],
        ["Natural frequency ωₙ","ωₙ = √(k/M)  [rad/s] — frequency of undamped oscillation"],
        ["Damping ratio ζ","ζ = b/(2√kM) = b/(2Mωₙ)\nζ<1: underdamped,  ζ=1: critical,  ζ>1: overdamped"],
        ["Multiple masses","For n masses: n coupled 2nd-order ODEs → 2n state variables\nSolve simultaneously in Laplace domain"],
      ]}/>

      <h2 style={S.h2}>Mechanical Rotational Systems</h2>
      <p style={S.p}>
        Rotational systems are the direct analogue of translational systems, with torque replacing force, moment of inertia replacing mass, and angular displacement replacing linear displacement. Newton's second law for rotation gives the identical mathematical structure.
      </p>
      <DiagramMechanicalRotational/>
      <Table2 rows={[
        ["Torque balance","Στ = J·α   →   J·θ̈ + b·θ̇ + Kθ·θ = τ(t)\nApply about the rotational axis of each rigid body"],
        ["Inertia element (J)","Torque: τJ = J·θ̈  →  TJ(s) = Js²Θ(s)\nStores kinetic energy: KE = ½Jω²\nUnits: kg·m²"],
        ["Rotational damper (b)","Torque: τb = b·θ̇  →  TB(s) = bsΘ(s)\nModels bearing friction, fluid drag\nUnits: N·m·s/rad"],
        ["Torsional spring (Kθ)","Torque: τk = Kθ·θ  →  TK(s) = KθΘ(s)\nModels shaft flexibility, rubber couplings\nUnits: N·m/rad"],
        ["Transfer function","G(s) = Θ(s)/T(s) = 1/(Js² + bs + Kθ)\nIdentical form to translational — same analysis applies"],
        ["Gear trains","Gear ratio N = N₂/N₁ = θ₁/θ₂ = τ₂/τ₁\nReflected inertia: J_ref = J_load / N²\nAllows lumping of geared inertias to one shaft"],
      ]}/>

      <h2 style={S.h2}>Electrical Systems</h2>
      <p style={S.p}>
        Electrical networks are modelled by applying Kirchhoff's Voltage Law (KVL: sum of voltages around a loop = 0) or Kirchhoff's Current Law (KCL: sum of currents into a node = 0). Working in the s-domain with impedances converts the circuit into an algebraic network, and standard circuit analysis (voltage divider, mesh analysis, nodal analysis) yields the transfer function directly.
      </p>
      <DiagramElectrical/>
      <Table2 rows={[
        ["Resistor R","Impedance: ZR = R  (frequency-independent)\nTime domain: vR = R·i,  Energy: P = i²R  (dissipated)"],
        ["Inductor L","Impedance: ZL = Ls\nTime domain: vL = L·di/dt,  Energy: WL = ½Li²  (magnetic)"],
        ["Capacitor C","Impedance: ZC = 1/(Cs)\nTime domain: iC = C·dv/dt,  Energy: WC = ½Cv²  (electric)"],
        ["Series RLC","Z_total = R + Ls + 1/(Cs)\nI(s)/V(s) = Cs/(LCs² + RCs + 1)\nωₙ = 1/√LC,  ζ = R/(2)·√(C/L)"],
        ["KVL (mesh analysis)","Sum of voltage drops around each mesh = 0\nLeads to simultaneous equations in mesh currents"],
        ["KCL (nodal analysis)","Sum of currents into each node = 0\nLeads to simultaneous equations in node voltages"],
        ["Force-voltage analogy","V↔f,  i↔ẋ,  L↔M,  R↔b,  1/C↔k,  q↔x\nIdentical ODE: L·q̈ + R·q̇ + (1/C)·q = v"],
        ["Force-current analogy","I↔f,  V↔ẋ,  C↔M,  1/R↔b,  1/L↔k\nParallel RLC is analogous to mass-spring-damper"],
      ]}/>

      <h2 style={S.h2}>Electromechanical Systems — DC Motor</h2>
      <p style={S.p}>
        Electromechanical systems couple two physical domains. The armature-controlled DC motor is the most important example: an electrical circuit (armature) drives a mechanical rotor through the motor torque constant Kt. The coupling creates a back-EMF Eb = Kb·ω that feeds back into the electrical circuit, creating an inherent feedback mechanism even before any external controller is added.
      </p>
      <DiagramDCMotor/>
      <Table2 rows={[
        ["Armature circuit (KVL)","Va = La·(dia/dt) + Ra·ia + Eb\nLaplace: Va(s) = (Las + Ra)Ia(s) + KbΩ(s)"],
        ["Rotor (Newton)","J·(dω/dt) + bm·ω = Tm = Kt·ia\nLaplace: (Js + bm)Ω(s) = KtIa(s)"],
        ["Motor torque constant Kt","Tm = Kt·ia  [N·m/A]\nIn SI: Kt = Kb (same numerical value)"],
        ["Back-EMF constant Kb","Eb = Kb·ω  [V·s/rad]\nBack-EMF opposes applied voltage; acts as speed feedback"],
        ["Overall TF Ω(s)/Va(s)","Kt / [(Las+Ra)(Js+bm) + KtKb]\nSimplified (La→0): Kt/Ra / [s(Js + bm + KtKb/Ra)]"],
        ["Position TF θ(s)/Va(s)","Divide Ω(s)/Va(s) by s — adds another integrator\nTypically 2nd order; used in servo/positioning control"],
        ["Electrical time constant","τe = La/Ra — governs current rise speed\nOften τe << τm, so La is neglected"],
        ["Mechanical time constant","τm = J·Ra/(KtKb + bm·Ra) — governs speed response"],
        ["Field-controlled DC motor","Armature current fixed; field current is input\nSimpler model: no back-EMF coupling; G(s) = K/(Js+bm)"],
      ]}/>

      <h2 style={S.h2}>Thermal &amp; Fluid Systems</h2>
      <p style={S.p}>
        Thermal and fluid systems are modelled using energy balance and conservation of mass respectively. Both yield first-order transfer functions — the direct analogue of a single-pole RC electrical circuit. The thermal resistance-capacitance model is widely used for heat exchangers, building HVAC, and electronic thermal management.
      </p>
      <DiagramThermalFluid/>
      <Table2 rows={[
        ["Thermal resistance Rth","Opposes heat flow: q = ΔT/Rth\nConduction: Rth = L/(kA)  |  Convection: Rth = 1/(hA)"],
        ["Thermal capacitance Cth","Stores thermal energy: Q = Cth·T\nCth = m·cp  (mass × specific heat)"],
        ["Thermal TF","Θ(s)/Q(s) = Rth/(τs+1)  where τ = RthCth\nDC gain = Rth;  pole at s = −1/τ"],
        ["Fluid resistance Rf","Linear valve: q₀ = h/Rf  (turbulent: q₀ ∝ √h — nonlinear!)\nRf = ΔP / q  [m⁻¹·s⁻¹] — linearised"],
        ["Fluid capacitance (tank)","A = cross-sectional area — acts as fluid capacitor\nA·dh/dt = qin − qout — continuity equation"],
        ["Liquid-level TF","H(s)/Qin(s) = Rf/(ARfs+1) = Rf/(τs+1)\nτ = ARf  |  Same first-order form as thermal"],
        ["Two-tank system","Two first-order systems in series: G(s) = K/[(τ₁s+1)(τ₂s+1)]\nInteraction between tanks may alter τ values"],
        ["Pneumatic system","Pressure P ↔ voltage V,  flow q ↔ current i\nCompressibility provides capacitance; pipes provide resistance"],
      ]}/>

      <h2 style={S.h2}>State-Space Representation</h2>
      <p style={S.p}>
        State-space is the most general modeling framework. Any nth-order system can be written as n coupled first-order ODEs using n state variables — physical quantities that store energy (velocities, displacements, currents, charges, temperatures). State-space is essential for multi-input multi-output (MIMO) systems, controller design (pole placement, LQR), and observer (Kalman filter) design.
      </p>
      <DiagramStateSpace/>
      <Table2 rows={[
        ["State variables x","Minimum set of variables that completely describe the system's future behaviour\nTypically: positions, velocities, currents, charges, temperatures\nn = system order = number of state variables"],
        ["State equation ẋ=Ax+Bu","A [n×n]: system (state) matrix — governs internal dynamics\nB [n×m]: input matrix — how inputs affect states\nẋ = Ax + Bu is a vector ODE"],
        ["Output equation y=Cx+Du","C [p×n]: output matrix — which states (or combinations) are measured\nD [p×m]: feedthrough — direct input→output path (usually D=0)"],
        ["Characteristic equation","det(sI − A) = 0  →  eigenvalues of A = open-loop poles\nStability: all eigenvalues must have Re < 0"],
        ["Transfer function from SS","G(s) = C(sI−A)⁻¹B + D\nConversion is always possible for LTI systems"],
        ["Controllability","System is controllable if rank[B  AB  A²B … Aⁿ⁻¹B] = n\nMust be controllable to place all poles by state feedback"],
        ["Observability","System is observable if rank[C; CA; CA²; … CAⁿ⁻¹] = n\nMust be observable to reconstruct all states from outputs"],
        ["Phase variables (canonical)","x₁=y, x₂=ẏ, x₃=ÿ … — derivatives as state variables\nSimplest way to convert a transfer function to state-space"],
        ["Companion form","Alternative: convert denominator coefficients directly to A matrix\nUsed in control canonical and observable canonical forms"],
      ]}/>

      <h2 style={S.h2}>Linearisation of Nonlinear Systems</h2>
      <p style={S.p}>
        Virtually all real physical systems are nonlinear: saturation, dead zones, friction, square-law fluid flow, and trigonometric nonlinearities all appear in practice. Linear control design methods require a linear model. Linearisation via Taylor expansion produces a valid linear approximation around a chosen operating (equilibrium) point for small perturbations.
      </p>
      <DiagramLinearisation/>
      <Table2 rows={[
        ["Operating point (equilibrium)","A steady-state where all derivatives are zero: f(x₀,u₀) = 0\nLinearisation is only valid in a neighbourhood of this point"],
        ["Taylor expansion (1st order)","f(x,u) ≈ f(x₀,u₀) + ∂f/∂x·Δx + ∂f/∂u·Δu\nΔx = x−x₀,  Δu = u−u₀ (perturbation variables)"],
        ["Jacobian matrix A","A = ∂f/∂x evaluated at (x₀,u₀)\n[n×n] matrix of partial derivatives"],
        ["Jacobian matrix B","B = ∂f/∂u evaluated at (x₀,u₀)\n[n×m] matrix of partial derivatives"],
        ["Linearised ODE","Δẋ = AΔx + BΔu,  Δy = CΔx + DΔu\nValid only for small Δx, Δu"],
        ["Example: pendulum","ẍ + (g/L)sinθ = τ/mL²   — nonlinear\nAround θ₀=0: sinθ≈θ  →  ẍ + (g/L)θ = τ/mL² (linear)\nAround θ₀=π (inverted): different linearisation, unstable"],
        ["Gain scheduling","Use multiple linearised models at different operating points\nSwitch or interpolate between controllers as operating point changes"],
        ["Describing function","Frequency-domain quasi-linearisation for sinusoidal inputs\nUsed to predict limit cycles in nonlinear control loops"],
      ]}/>

      <h2 style={S.h2}>Cross-Domain System Analogies</h2>
      <p style={S.p}>
        One of the most powerful insights in systems theory is that all physical domains share the same mathematical structure. By identifying analogous quantities across domains, a model from one domain can be immediately translated to another, and the same analysis methods apply universally.
      </p>
      <DiagramAnalogies/>
      <Table2 rows={[
        ["Through variable","Flows through an element: force, torque, current, heat flow, fluid flow\nMeasured by 'breaking' the branch (series measurement)"],
        ["Across variable","Measured across an element: velocity, ang. velocity, voltage, temp. diff., pressure\nMeasured in parallel (between two nodes)"],
        ["Inertance (I)","Stores energy proportional to through variable squared\nMass M,  Inertia J,  Inductance L,  Thermal: none exact"],
        ["Dissipance (D)","Dissipates power: damper b, R, Rth, Rf\nGoverned by through = D × across"],
        ["Compliance (C)","Stores energy proportional to across variable squared\nSpring 1/k, Capacitor C, Thermal capacitance Cth, Tank area A"],
        ["Universal TF","G(s) = 1/(Is² + Ds + 1/C)\nωₙ = √(1/IC),  ζ = D/(2)·√(C/I)\nApplies in ALL physical domains"],
        ["Analogue computer","Electrical circuits built to mimic mechanical/thermal systems\nHistorically used before digital simulation; still useful conceptually"],
        ["Bond graph","Unified graphical modeling tool for all physical domains\nPower = effort × flow  (generalises all domain analogies)"],
      ]}/>

      <h2 style={S.h2}>Transfer Function Derivation Methods</h2>
      <Table2 rows={[
        ["Differential equation → TF","Write governing ODE, Laplace transform (zero ICs), rearrange Y(s)/U(s)\nMost direct method for single-domain single-element systems"],
        ["Impedance method (electrical)","Replace elements with impedances Z(s); apply voltage/current divider\nFastest for electrical and analogous circuits"],
        ["Mason's gain formula (SFG)","Draw signal flow graph; apply T = (ΣPₖΔₖ)/Δ\nBest for complex multi-loop systems"],
        ["Block diagram reduction","Simplify using series/parallel/feedback rules step by step\nIntuitive for systems already described as block diagrams"],
        ["State-space → TF","G(s) = C(sI−A)⁻¹B + D\nRequired for MIMO systems; gives matrix of TFs"],
        ["Experimental identification","Step test: measure K, τ, L from step response\nFrequency sweep: measure Bode plot from sinusoidal input\nUsed when first-principles model is unavailable"],
        ["System identification (ARX/ARMAX)","Data-driven: fit parametric model to input-output data\nMATLAB System Identification Toolbox; subspace methods for SS"],
        ["Finite element / simulation","For complex geometries: FEA gives high-order models\nMust be model-order-reduced before control design"],
      ]}/>

      <h2 style={S.h2}>Modeling Assumptions &amp; Validity</h2>
      <Table2 rows={[
        ["Linearity","Real systems are nonlinear; linear models valid only near operating point\nCheck: is the output proportional to input? Does superposition hold?"],
        ["Time invariance","Parameters (M, k, b, R, L) assumed constant\nViolated by: wear, temperature drift, fuel consumption changing mass"],
        ["Zero initial conditions","TF G(s) = Y(s)/U(s) assumes all ICs are zero\nNon-zero ICs appear as additional terms; handled in state-space"],
        ["Lumped parameters","Treat distributed systems as lumped (e.g. flexible beam as rigid)\nValid when physical dimension << wavelength of interest"],
        ["Rigid body assumption","Structures assumed rigid (no flexibility)\nFlexible modes must be included if resonance is near control bandwidth"],
        ["Ideal elements","Perfect springs (massless), perfect dampers (zero spring), perfect sensors\nReal elements have parasitic effects: spring has mass, etc."],
        ["Model order reduction","High-order models reduced by: removing fast poles (far left in s-plane),\nBalanced truncation, or Padé approximation for time delays"],
        ["Validation","Compare model step response to physical system step response\nCheck: correct DC gain, natural frequency, damping, time constants"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Translational: M·ẍ + b·ẋ + k·x = f(t)  →  G(s) = 1/(Ms² + bs + k)",
        "Rotational: J·θ̈ + b·θ̇ + Kθ·θ = τ(t)  →  G(s) = 1/(Js² + bs + Kθ)",
        "Series RLC: G(s) = Vc(s)/V(s) = 1/(LCs² + RCs + 1)   ωₙ=1/√LC, ζ=R/2·√(C/L)",
        "DC Motor: Ω(s)/Va(s) = Kt/[(Las+Ra)(Js+bm)+KtKb]",
        "Thermal/Fluid (1st order): G(s) = K/(τs+1)   τ=RthCth  or  τ=ARf",
        "State-space → TF: G(s) = C(sI−A)⁻¹B + D   |   Poles = eigenvalues of A",
        "Linearisation: Δẋ = AΔx + BΔu   A=∂f/∂x|(x₀,u₀),  B=∂f/∂u|(x₀,u₀)",
        "Natural frequency & damping (all domains): ωₙ = √(1/IC),   ζ = D/(2√(I/C))",
      ]}/>
    </div>
  );
}